"use client";

import React from "react";
import { Facebook, Instagram, Loader, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="193765" className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted/50 animate-pulse rounded-full" />)}
      </div>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group">
            <span className="h-1.5 w-1.5 rounded-full bg-secondary scale-0 group-hover:scale-100 transition-transform" />
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="p-4 bg-success/10 border-2 border-success/20 rounded-2xl">
        <p className="text-sm font-semibold text-success">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex gap-2">
              <FormControl>
                <Input type="email" placeholder="Your email address" className="bg-white border-2 border-secondary/20 focus-visible:ring-secondary rounded-2xl" {...field} />
              </FormControl>
              <Button type="submit" disabled={isSubmitting} variant="default" className="rounded-2xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 hover:scale-105 transition-all">
                {isSubmitting ? <Loader className="size-4 animate-spin" /> : "Join Fun"}
              </Button>
            </div>
            <FormMessage className="mt-2 text-xs font-medium" />
          </FormItem>} />
    </Form>;
}
export default function TheFoundationFooter() {
  const socialLinks = [{
    icon: Twitter,
    href: "#",
    label: "Twitter",
    color: "bg-sky-400"
  }, {
    icon: Facebook,
    href: "#",
    label: "Facebook",
    color: "bg-blue-600"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram",
    color: "bg-rose-500"
  }];
  const paymentBadges = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/payment_visa.png",
    alt: "Visa"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/payment_mastercard.png",
    alt: "Mastercard"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/payment_amex.png",
    alt: "American Express"
  }];
  const companyLinks = [{
    label: "Our Journey",
    href: "#"
  }, {
    label: "Contact Us",
    href: "/ContactPage"
  }];
  return <section id="learning-portal-footer" className="bg-[#f5f0ff] text-foreground pt-20 pb-10 px-6 md:px-12 relative overflow-hidden">
      {}
      <div className="absolute top-10 left-10 opacity-10 pointer-events-none">
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
      </div>
      <div className="absolute bottom-20 right-10 opacity-10 pointer-events-none rotate-12">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {}
          <div className="flex flex-col gap-6">
            <Link to="/HomePage" className="inline-block hover:scale-105 transition-transform">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/trustcart_logo_footer.png" alt="LinguaKids" className="h-10 w-auto" />
            </Link>
            <p className="text-base font-medium text-muted-foreground leading-relaxed">
              A magical Spanish learning adventure for kids! We make every lesson a joyful journey of discovery, confidence, and play.
            </p>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-xl font-serif text-accent-foreground tracking-tight">Adventure</h3>
            <MenuProvider menu_id="138">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-xl font-serif text-accent-foreground tracking-tight">Parents</h3>
            <ul className="flex flex-col gap-3">
              {companyLinks.map((link, i) => <li key={link.label} data-index={i}>
                  <Link to={link.href} className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors flex items-center gap-2 group">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary scale-0 group-hover:scale-100 transition-transform" />
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <div className="space-y-2">
              <h3 className="text-xl font-serif text-accent-foreground tracking-tight">Say Hola</h3>
              <p className="text-sm font-medium text-muted-foreground">
                Join our family newsletter for playful learning tips and special class offers!
              </p>
            </div>
            <FormProvider formKey="newsletter_signup_learning_portal" formId="newsletter_signup_learning_portal" sectionName="TheFoundationFooter" defaultValues={{
            email: ""
          }} submitText="Join Fun" successMessage="¡Gracias! You're on the list!">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <Separator className="bg-accent/20 mb-8 h-0.5 rounded-full" />

        {}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-sm font-medium text-muted-foreground order-3 md:order-1">
            © {new Date().getFullYear()} LinguaKids. Hand-crafted with love for little learners. 
          </p>

          <div className="flex items-center gap-4 order-2">
            {socialLinks.map((social, i) => {
            const Icon = social.icon;
            return <Link key={social.label} data-index={i} to={social.href} className={`flex items-center justify-center size-10 rounded-full bg-white shadow-sm hover:shadow-md hover:scale-110 transition-all border-2 border-accent/10 text-muted-foreground hover:text-primary`} aria-label={social.label}>
                  <Icon className="size-5" />
                </Link>;
          })}
          </div>

          <div className="flex items-center gap-3 order-1 md:order-3">
            {paymentBadges.map((badge, i) => <div key={badge.alt} data-index={i} className="bg-white p-1.5 rounded-xl border-2 border-accent/5 flex items-center justify-center shadow-sm">
                <img src={badge.src} alt={badge.alt} className="h-5 w-auto opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all" />
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}